import { Euler, MathUtils, Object3D, Quaternion, Vector2, Vector3 } from 'three'
import Controls from './Controls'
import { isIOS } from 'react-device-detect'

interface CameraDeviceOrientationParams {
  worldRange?: Vector2 // the range in the real world of the device
  virtualRange?: Vector2 // the range in the virtual world that "world range" maps on to
  hardVirtualRange?: Vector2 // hardVirtualRange clamps that range. So if world range y is 90, then we can't rotate past 90
  yRotationTarget: Object3D
  xRotationTarget: Object3D

  enableRecenter?: boolean // If true, camera will recenter
}

export default class GyroCameraBehaviour extends Controls {
  dummyObject: Object3D = new Object3D()
  initialDummyRotation: Euler | null = null
  prevDeviceRotation = new Vector3(0, 0, 0)
  rotationCycleCount = new Vector3(0, 0, 0)
  worldRange: Vector2 = new Vector2(
    MathUtils.degToRad(90),
    MathUtils.degToRad(45)
  )

  outOfRangeElapsed = 0
  outOfRangeLimit = 3
  protected readonly yRotateAxis: Object3D
  protected readonly xRotateAxis: Object3D
  private enabled: boolean = false
  private enableRecenter: boolean = false
  private alphaOffset: number = 0 // radians
  private virtualRange = new Vector2(
    MathUtils.degToRad(25),
    MathUtils.degToRad(45)
  )

  private hardVirtualRange: Vector2 = new Vector2(
    MathUtils.DEG2RAD * 20,
    MathUtils.DEG2RAD * 999999
  )

  private staticRotationOffset: Euler = new Euler(
    MathUtils.degToRad(0),
    MathUtils.degToRad(180),
    0)

  private uninitializedRotationOffset: Euler = new Euler(
    MathUtils.degToRad(90),
    MathUtils.degToRad(180),
    0)

  private rotationAxisOffsetEnabled: boolean[] = [false, true, false]
  private currentRotationOffset: Euler = new Euler()
  private _zee = new Vector3(0, 0, 1)
  private _q0 = new Quaternion()
  private _q1 = new Quaternion(-Math.sqrt(0.5), 0, 0, Math.sqrt(0.5)) // - PI/2 around the x-axis

  constructor (params: CameraDeviceOrientationParams) {
    super()
    if (params.virtualRange != null) this.virtualRange = params.virtualRange
    if (params.worldRange != null) this.worldRange = params.worldRange
    if (params.hardVirtualRange != null) this.hardVirtualRange = params.hardVirtualRange

    this.enableRecenter = params.enableRecenter ?? false

    this.dummyObject.name = 'dummyRotation'
    this.dummyObject.rotation.reorder('YXZ')
    this.xRotateAxis = params.xRotationTarget
    this.yRotateAxis = params.yRotationTarget

    registeredControls.push(this)
    initializeOrientationControls()
  }

  public get isEnabled (): boolean {
    return this.enabled
  }

  private get CurrentDeviceOrientation (): Euler {
    if (this.initialDummyRotation === null &&
      !(this.dummyObject.rotation.x * MathUtils.RAD2DEG === -90 &&
        this.dummyObject.rotation.y * MathUtils.RAD2DEG === 0 &&
        this.dummyObject.rotation.z * MathUtils.RAD2DEG === 0)) {
      this.initialDummyRotation = this.dummyObject.rotation.clone()
    }

    const eul1 = this.dummyObject.rotation
    const eul2 = (this.initialDummyRotation != null) ? this.staticRotationOffset : this.uninitializedRotationOffset
    const eul3 =
      (this.initialDummyRotation != null)
        ? new Euler(
          (this.rotationAxisOffsetEnabled[0]) ? -this.initialDummyRotation.x : 0,
          (this.rotationAxisOffsetEnabled[1]) ? -this.initialDummyRotation.y : 0,
          (this.rotationAxisOffsetEnabled[2]) ? -this.initialDummyRotation.z : 0)
        : new Euler(0, 0, 0)
    const summedEular = new Euler()
    summedEular.x = eul1.x + eul2.x + eul3.x
    summedEular.y = eul1.y + eul2.y + eul3.y
    summedEular.z = eul1.z + eul2.z + eul3.z

    return summedEular
  }

  enable (): void {
    this.currentRotationOffset = new Euler(0, 0, 0)
    // this.controls = new DeviceOrientationControls(this.dummyObject)

    this.yRotateAxis.position.set(0, 0, 0)
    this.yRotateAxis.rotation.set(0, 0, 0)
    this.xRotateAxis.position.set(0, 0, 0)
    this.xRotateAxis.rotation.set(0, 0, 0)

    this.enabled = true
  }

  disable (): void {
    this.enabled = false
  }

  public dispose (): void {
  }

  public updateOrientation = (event: DeviceOrientationEvent) => {
    const alpha = event.alpha ? MathUtils.degToRad(event.alpha) + this.alphaOffset : 0 // Z
    const beta = event.beta ? MathUtils.degToRad(event.beta) : 0 // X'
    const gamma = event.gamma ? MathUtils.degToRad(event.gamma) : 0 // Y''

    const screenOrientation = isIOS ? window.orientation : screen.orientation.angle // while window.orientation is depreciated, its still used by ios apparently
    const orient = screenOrientation ? MathUtils.degToRad(screenOrientation) : 0 // O

    this.setObjectQuaternion(alpha, beta, gamma, orient)
  }

  public updateCameraRotation (_deltaTime: number): void {
    const unprocessedDeviceRotation = this.CurrentDeviceOrientation

    // before we do any processing, this maps device orientation to normal degrees to make it easier for use to do processing
    const affectiveDeviceRotation = new Euler(
      unprocessedDeviceRotation.x,
      unprocessedDeviceRotation.y + MathUtils.degToRad(180),
      unprocessedDeviceRotation.z
    )

    // this ensures the rotation cycles add up, instead of flipping around to the other end of the rotation range
    // (if move to 361 degrees for example)
    if (Math.abs(affectiveDeviceRotation.y - this.prevDeviceRotation.y) > MathUtils.degToRad(180)) {
      this.rotationCycleCount.y -= this.sign(affectiveDeviceRotation.y - this.prevDeviceRotation.y)
    }
    if (Math.abs(affectiveDeviceRotation.x - this.prevDeviceRotation.x) > MathUtils.degToRad(180)) {
      this.rotationCycleCount.x -= this.sign(affectiveDeviceRotation.x - this.prevDeviceRotation.y)
    }
    if (Math.abs(affectiveDeviceRotation.z - this.prevDeviceRotation.z) > MathUtils.degToRad(180)) {
      this.rotationCycleCount.x -= this.sign(affectiveDeviceRotation.z - this.prevDeviceRotation.z)
    }

    this.prevDeviceRotation.set(
      affectiveDeviceRotation.x,
      affectiveDeviceRotation.y,
      affectiveDeviceRotation.z)

    affectiveDeviceRotation.set(
      affectiveDeviceRotation.x + this.rotationCycleCount.x * MathUtils.degToRad(360),
      affectiveDeviceRotation.y + this.rotationCycleCount.y * MathUtils.degToRad(360),
      affectiveDeviceRotation.z + this.rotationCycleCount.z * MathUtils.degToRad(360)
    )

    // this allows use to customize the sensitivities of gyro data, so we can make different axis more/less sensitive
    const mappedOrientation = new Vector3(
      (affectiveDeviceRotation.x / this.worldRange.x) * this.virtualRange.x,
      (affectiveDeviceRotation.y / this.worldRange.y) * this.virtualRange.y,
      affectiveDeviceRotation.z
    )

    const projectedDir = new Vector3(
      mappedOrientation.x + this.currentRotationOffset.x,
      mappedOrientation.y + this.currentRotationOffset.y,
      mappedOrientation.z + this.currentRotationOffset.z
    )

    // here we impose a clamp if we want once
    const xBeyondLimit = (Math.abs(projectedDir.x) > this.hardVirtualRange.x / 2)
    const yBeyondLimit = (Math.abs(projectedDir.y) > this.hardVirtualRange.y / 2)
    const isBeyondLimits = xBeyondLimit || yBeyondLimit

    this.outOfRangeElapsed += ((isBeyondLimits) ? 1 : -2.5) * _deltaTime
    this.outOfRangeElapsed = Math.max(this.outOfRangeElapsed, 0)
    this.outOfRangeElapsed = Math.min(this.outOfRangeElapsed, 4)

    if (this.enableRecenter && this.outOfRangeElapsed > this.outOfRangeLimit) {
      this.currentRotationOffset.y = MathUtils.lerp(
        this.currentRotationOffset.y,
        -mappedOrientation.y,
        0.1)

      this.currentRotationOffset.x = MathUtils.lerp(
        this.currentRotationOffset.x,
        -mappedOrientation.x,
        0.1)
    }

    projectedDir.add(new Vector3(MathUtils.DEG2RAD * 180, MathUtils.DEG2RAD * 180, MathUtils.DEG2RAD * 180))
    projectedDir.set(
      this.wrap(projectedDir.x, MathUtils.degToRad(0), MathUtils.degToRad(360)),
      this.wrap(projectedDir.y, MathUtils.degToRad(0), MathUtils.degToRad(360)),
      this.wrap(projectedDir.z, MathUtils.degToRad(0), MathUtils.degToRad(360))
    )

    this.clampVector(projectedDir,
      new Vector3(MathUtils.DEG2RAD * 180 - this.hardVirtualRange.x / 2, MathUtils.DEG2RAD * 180 - this.hardVirtualRange.y / 2, MathUtils.DEG2RAD * 360),
      new Vector3(MathUtils.DEG2RAD * 180 + this.hardVirtualRange.x / 2, MathUtils.DEG2RAD * 180 + this.hardVirtualRange.y / 2, MathUtils.DEG2RAD * 360)
    )

    projectedDir.sub(new Vector3(MathUtils.DEG2RAD * 180, MathUtils.DEG2RAD * 180, MathUtils.DEG2RAD * 180))

    this.yRotateAxis.rotation.y = this.WrapLerp(
      MathUtils.degToRad(-180),
      MathUtils.degToRad(180),
      this.yRotateAxis.rotation.y,
      projectedDir.y,
      0.1)
    this.xRotateAxis.rotation.x = this.WrapLerp(
      MathUtils.degToRad(-180),
      MathUtils.degToRad(180),
      this.xRotateAxis.rotation.x,
      projectedDir.x,
      0.1)
  }

  private setObjectQuaternion = (alpha: number, beta: number, gamma: number, orient: number) => {
    const euler = new Euler(beta, alpha, -gamma, 'YXZ') // 'ZXY' for the device, but 'YXZ' for us
    this.dummyObject.quaternion.setFromEuler(euler) // orient the device
    this.dummyObject.quaternion.multiply(this._q1) // camera looks out the back of the device, not the top
    this.dummyObject.quaternion.multiply(this._q0.setFromAxisAngle(this._zee, -orient)) // adjust for screen orientation
  }
}

const registeredControls: GyroCameraBehaviour[] = []

function initializeOrientationControls () {
  window.addEventListener('deviceorientation', orientationChangeEventHandler)
}

function orientationChangeEventHandler (event: DeviceOrientationEvent) {
  registeredControls.forEach(x => {
    if (x.isEnabled) x.updateOrientation(event)
  })
}
